import type { OfflineReplayRequest } from '@/types'
import {
  isNothingToBillReplayError,
  isTerminalReplayError,
  pendingOfflineOrders,
  retryBackoffMs,
  updateOfflineOrder,
  type OfflineOrder,
} from './offlineStore'

/**
 * Replaying the offline queue on reconnect.
 *
 * The guiding rule: work that has already reached the kitchen must never be
 * discarded silently. Only two outcomes end an operation — `synced`, where the
 * order exists server-side, and `unreconciled`, which is not a drop but a
 * handover to a supervised queue where a manager bills or writes it off with an
 * audit trail. Everything else retries.
 */

export type ReplayOutcome = 'synced' | 'retry' | 'unreconciled' | 'discarded'

/**
 * Decide what a replay result means for one queued operation.
 *
 * `offline_order_duplicate` is a SUCCESS: it means the first attempt actually
 * landed and the server already holds the order. Treating it as a failure
 * would strand a perfectly good order in the queue forever.
 *
 * Anything unrecognised retries. An unknown error must never discard a ticket
 * that is already in the pass.
 *
 * F-FE-N160 added `discarded`, the third terminal outcome. The two that existed
 * could not describe a permanent refusal with nothing left to bill, so such an
 * entry fell through to `retry` and stayed there — a card the drain could never
 * clear and the tray offered no way to remove. See isNothingToBillReplayError.
 */
export function classifyReplayOutcome(errorCode: string | null): ReplayOutcome {
  if (errorCode == null) return 'synced'
  if (errorCode === 'offline_order_duplicate') return 'synced'
  if (isNothingToBillReplayError(errorCode)) return 'discarded'
  if (isTerminalReplayError(errorCode)) return 'unreconciled'
  return 'retry'
}

/** The wire shape lives in @/types so the API client and this engine agree. */
export type ReplayRequest = OfflineReplayRequest

export interface ReplayDeps {
  /**
   * Resolves with the stable error code, or null on success. Must not throw.
   *
   * `needsFire` is set when the order synced but the server could NOT mark it
   * fired (F-CTRL-N54). That is not a failure — the order exists — but it must
   * not be silent: the next person to tap "Add items & fire KOT" on that order
   * would print a slip for food the kitchen already made.
   */
  replay: (
    req: ReplayRequest,
    entry: OfflineOrder,
  ) => Promise<{ errorCode: string | null; needsFire?: boolean; needsAttention?: boolean }>
  /**
   * Hand a permanently-failed operation to the server-side reconciliation
   * queue. Without this the queue stays empty, Day Close never blocks, and the
   * food that was fired but never billed is invisible to anyone but this
   * terminal — which is the whole failure this feature exists to prevent.
   *
   * Must not throw; a failed report leaves the entry pending so it is retried.
   */
  report: (op: OfflineOrder, failureCode: string) => Promise<boolean>
  /** Defaults to Date.now. */
  clock?: ReplayClock
}

export interface ReplaySummary {
  synced: number
  retry: number
  unreconciled: number
  /**
   * Entries permanently refused with nothing left to bill (F-FE-N160). Counted
   * separately from `unreconciled` because no manager action follows one.
   */
  discarded: number
  /**
   * Pending entries skipped because their backoff had not elapsed (F-FE-N75).
   * Reported so a caller can tell "nothing left to do" from "not yet" — the
   * difference between a queue that is drained and one that is stuck.
   */
  deferred: number
  /**
   * Orders that synced but still need a manual fire (F-CTRL-N54). Surfaced so
   * the cashier is told, instead of finding out as a duplicate kitchen ticket.
   */
  needsFire: number
  /**
   * Orders that synced but raised something a manager must resolve (F-CTRL-N55)
   * — a settlement that failed, or a server total that disagreed with what was
   * collected offline. The row is already in the reconciliation queue and Day
   * Close will block on it; this only makes it visible at the moment it happens.
   */
  needsAttention: number
}

/** Injected so the retry schedule is testable without faking the clock. */
export interface ReplayClock {
  now: () => number
}

/** Shape one queued entry into the wire request. */
export function toReplayRequest(op: OfflineOrder): ReplayRequest {
  return {
    op_id: op.opId,
    // F-CTRL-N52: the ticket the slip printed. The server records it for
    // slip-to-order matching and allocates the real order number itself.
    ticket_ref: op.ticketRef,
    // Only ever set on an entry queued by a pre-N52 bundle that upgraded
    // mid-outage: that slip really does carry a reserved number, so the order
    // must keep it. The server validates it before honouring it — this client
    // does not get to name an invoice number.
    ...(op.orderNumber ? { order_number: op.orderNumber } : {}),
    captured_at: op.capturedAt,
    order_type: op.orderType,
    table_id: op.tableId,
    // F-CTRL-N67: floor entries only. Absent on a counter entry, so its wire
    // shape is unchanged.
    ...(op.guestCount != null ? { guest_count: op.guestCount } : {}),
    ...(op.source ? { source: op.source } : {}),
    ...(op.targetOrderId ? { target_order_id: op.targetOrderId } : {}),
    // F-MM-N26: the tender ALREADY taken during the outage, so the server
    // prices the order at that rate instead of the cash placeholder.
    //
    // Without it, the order came back priced at cash, settleOfflinePayment
    // tendered exactly that figure, ProcessPayment repriced to the real tender,
    // and the amount was then exactly the pre-reprice total — the precise
    // signature of the F-MM-N7 anti-fraud guard. Every offline payment 409'd:
    // cash in the drawer, order unpaid, Day Close blocked.
    //
    // Omitted for an unpaid entry, and that is the point: an order fired but
    // not paid must stay unlocked so the tender is chosen at the counter.
    ...(op.payment ? { paid_with: op.payment.method } : {}),
    lines: op.lines.map((l) => ({
      product_id: l.productId,
      quantity: l.quantity,
      ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
      // Ids only — the server reprices from live modifier_options on replay.
      ...(l.selections && l.selections.length > 0
        ? {
            modifiers: l.selections.map((sel) => ({
              option_id: sel.option_id,
              quantity: sel.quantity,
            })),
          }
        : {}),
    })),
  }
}

/**
 * Replay every pending operation in CAPTURE ORDER.
 *
 * Order matters: entries are replayed oldest-first so the day each was taken on
 * is resolved in the sequence it happened, and so a future add-items operation
 * can never overtake the create it depends on.
 *
 * Runs sequentially rather than in parallel. A burst of concurrent replays
 * against a backend that has just come back is exactly when it is least able to
 * absorb one, and the queue is small by construction.
 */
export async function replayOfflineQueue(deps: ReplayDeps): Promise<ReplaySummary> {
  const summary: ReplaySummary = { synced: 0, retry: 0, unreconciled: 0, discarded: 0, deferred: 0, needsFire: 0, needsAttention: 0 }
  const now = deps.clock?.now() ?? Date.now()

  // F-FE-N75: entries still inside their backoff window are skipped, not
  // attempted. Without this a caller that drains on a timer would retry a
  // failing entry as fast as it ticked.
  const pending = await pendingOfflineOrders()
  const due = pending.filter((o) => (o.nextAttemptAt ?? 0) <= now)
  summary.deferred = pending.length - due.length

  for (const op of due) {
    let errorCode: string | null
    try {
      const r = await deps.replay(toReplayRequest(op), op)
      errorCode = r.errorCode
      if (r.needsFire) summary.needsFire++
      if (r.needsAttention) summary.needsAttention++
    } catch {
      // A thrown transport error is retryable, never terminal — the outage may
      // simply not be over yet.
      errorCode = 'network_error'
    }

    const outcome = classifyReplayOutcome(errorCode)
    summary[outcome]++

    if (outcome === 'synced') {
      await updateOfflineOrder(op.opId, { status: 'synced', lastError: null })
    } else if (outcome === 'discarded') {
      // F-FE-N160: permanently refused, and nothing outstanding to bill.
      //
      // No report() call, deliberately: the reconciliation queue is for food
      // that was fired and never charged, and this entry has none. It also
      // rejects an empty line list outright, so reporting could only fail and
      // drop the entry back into the retry loop.
      //
      // This is the branch that CLEARS DEVICES ALREADY STUCK — including the
      // Mizaj till — on their next drain, with nobody touching the terminal.
      await updateOfflineOrder(op.opId, { status: 'discarded', lastError: errorCode })
    } else if (outcome === 'unreconciled') {
      // Only mark it locally resolved once the SERVER has the item. If the
      // report fails, leave the entry pending so the next drain tries again —
      // a local "unreconciled" flag nobody else can see is no better than
      // dropping the food on the floor.
      const reported = await deps.report(op, errorCode ?? 'unknown_error')
      if (reported) {
        await updateOfflineOrder(op.opId, { status: 'unreconciled', lastError: errorCode })
      } else {
        summary.unreconciled--
        summary.retry++
        await deferRetry(op, errorCode, now)
      }
    } else {
      await deferRetry(op, errorCode, now)
    }
  }

  return summary
}

/**
 * Record a failed attempt and schedule the next one.
 *
 * Kept as one function so the two failure paths — a transient error, and a
 * permanent error whose handover to the reconciliation queue itself failed —
 * can never drift into scheduling differently. Both are "try again later".
 */
async function deferRetry(
  op: OfflineOrder,
  errorCode: string | null,
  now: number,
): Promise<void> {
  const attempts = op.attempts + 1
  await updateOfflineOrder(op.opId, {
    attempts,
    lastError: errorCode,
    nextAttemptAt: now + retryBackoffMs(attempts),
  })
}
